import React, { useCallback, useState } from "react";
import {
  GoogleMap,
  useLoadScript,
  InfoBox,
  MarkerF,
} from "@react-google-maps/api";
import ScrollAnimation from "../components/ScrollAnimation";

import { useTranslation } from "react-i18next";

import PinIcon from "../images/icons/pin.svg";
import { billboardLocations } from "../data/billboardLocations";

export default function Map() {
  const { t } = useTranslation();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_API_KEY,
  });
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [center, setCenter] = useState({
    lat: 29.092047,
    lng: -110.957617,
  });

  const mapContainerStyle = {
    width: "100%",
    height: "40rem",
    borderRadius: "1rem",
  };

  const mapOptions = {
    disableDefaultUI: true, // Hides all HUD items like zoom controls and map type selector
    zoomControl: false, // Adds back only the zoom control
    styles: [
      {
        featureType: "poi",
        stylers: [{ visibility: "off" }], // Hides points of interest
      },
      {
        featureType: "road",
        elementType: "geometry",
        stylers: [{ color: "#ffffff" }], // Custom road color
      },
      {
        featureType: "water",
        stylers: [{ color: "#bde0ff" }], // Custom water color
      },
    ],
  };

  const handleMarkerClick = useCallback(
    (marker) => {
      if (selectedMarker && selectedMarker.id === marker.id) {
        setSelectedMarker(null);
        return;
      }
      setSelectedMarker(marker);
      setCenter(marker.location);
    },
    [selectedMarker]
  );

  const downloadPdf = (pdf) => {
    window.open(pdf, "_blank");
  };

  const PinModal = () => {
    const { image, code, size, pdf, address } = selectedMarker || {};

    if (!selectedMarker) return null; // Early return if no selectedMarker

    return (
      <div className="pinModal p-2 d-flex flex-column justify-content-between align-items-start info-box open">
        {image && (
          <img
            className="img-fluid mb-3"
            src={image}
            alt={code}
            loading="lazy"
          />
        )}
        <p>
          <span className="me-1">{t("code")}:</span>
          {code}
        </p>
        <p>
          <span className="me-1">{t("size")}:</span>
          {size}
        </p>
        {address && (
          <p>
            <span className="me-1">{t("address")}:</span>
            {address}
          </p>
        )}
        {pdf && (
          <button
            className="btn"
            type="button"
            onClick={() => downloadPdf(pdf)}
          >
            {t("downloadPdf")}
          </button>
        )}
      </div>
    );
  };

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <ScrollAnimation
      id="map"
      className="container pb-5 d-flex flex-column justify-content-center align-items-center"
      ariaLabelledBy="map-title"
    >
      <h2 className="text-center">
        {t("mapTitle")} <span>{t("mapTitle2")}</span>
      </h2>
      <p>{t("mapDescription")}</p>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        zoom={13}
        center={center}
        options={mapOptions}
        onIdle={() => setCenter(center)}
      >
        {billboardLocations.map((marker) => (
          <MarkerF
            key={marker.id}
            position={marker.location}
            onClick={() => handleMarkerClick(marker)}
            icon={{
              url: PinIcon,
              scaledSize: new window.google.maps.Size(40, 40),
            }}
          />
        ))}
        {selectedMarker && (
          <InfoBox
            key={selectedMarker.id}
            position={selectedMarker.location}
            onCloseClick={() => setSelectedMarker(null)}
            options={{
              closeBoxURL: "",
              enableEventPropagation: true,
              boxStyle: {
                width: "15rem",
                padding: "0",
                borderRadius: "5px",
              },
              pixelOffset: new window.google.maps.Size(-120, 5),
            }}
          >
            <PinModal />
          </InfoBox>
        )}
      </GoogleMap>
    </ScrollAnimation>
  );
}
